import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZES = [10, 25, 50, 100];

/**
 * Page numbers with ellipsis, so the control stays a fixed width however many
 * pages there are: 1 … 4 5 [6] 7 8 … 20
 */
const pageList = (page, totalPages) => {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);

  const pages = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(totalPages - 1, page + 1);

  if (start > 2) pages.push('…');
  for (let p = start; p <= end; p++) pages.push(p);
  if (end < totalPages - 1) pages.push('…');

  pages.push(totalPages);
  return pages;
};

const Pagination = ({
  page,
  totalPages,
  total,
  rangeStart,
  rangeEnd,
  pageSize,
  onPageChange,
  onPageSizeChange,
  label = 'items',
}) => {
  // A single page of results needs no controls.
  if (total === 0) return null;

  const buttonBase = 'inline-flex items-center justify-center min-w-[2rem] h-8 px-2 text-sm rounded-md border transition-colors';

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-3 border-t border-gray-200 bg-white">
      <div className="flex items-center gap-3">
        <span className="text-sm text-gray-500">
          Showing <span className="font-medium text-gray-900">{rangeStart}</span>–
          <span className="font-medium text-gray-900">{rangeEnd}</span> of{' '}
          <span className="font-medium text-gray-900">{total}</span> {label}
        </span>

        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            aria-label={`${label} per page`}
            className="text-sm border border-gray-300 rounded-md py-1 pl-2 pr-7 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size} / page</option>
            ))}
          </select>
        )}
      </div>

      {totalPages > 1 && (
        <nav className="flex items-center gap-1" aria-label="Pagination">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="Previous page"
            className={`${buttonBase} border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {pageList(page, totalPages).map((entry, i) =>
            entry === '…' ? (
              <span key={`gap-${i}`} className="px-1 text-sm text-gray-400 select-none">…</span>
            ) : (
              <button
                key={entry}
                type="button"
                onClick={() => onPageChange(entry)}
                aria-current={entry === page ? 'page' : undefined}
                className={`${buttonBase} ${
                  entry === page
                    ? 'border-blue-600 bg-blue-600 text-white font-medium'
                    : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {entry}
              </button>
            ),
          )}

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            aria-label="Next page"
            className={`${buttonBase} border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white`}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </nav>
      )}
    </div>
  );
};

export default Pagination;
